import React, { lazy, Suspense, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { LoadingSpinner } from 'components/common';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'helpers/dayjs';
import {
  getLogisticTrackings,
  setNestedParams,
  exportData,
} from 'redux/crm/logistic/logisticTrackingSlice';
import LogisticTrackingFilterAdvanceModal from 'components/app/crm/logistic/tracking/LogisticTrackingFilterAdvanceModal';
import LogisticTrackingFilterBar from 'components/app/crm/logistic/tracking/LogisticTrackingFilterBar';
import ImportLogisticTrackingDrawer from 'components/app/crm/logistic/tracking/ImportLogisticTrackingDrawer';
import { LuShip, LuAlertTriangle } from 'react-icons/lu';
import { FaArrowLeft, FaSyncAlt, FaFileExcel, FaFileImport } from 'react-icons/fa';
import { PERPAGE_DEFAULT_SM } from 'app_config';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { useShipScopes } from 'components/app/crm/logistic/shipScope';

const LazyLogisticTracking = lazy(() =>
  import('components/app/crm/logistic/tracking/LogisticTrackingList')
);

const ENTRY_KEY = 'allData';
const EMPTY_RANGE = { startDate: '', endDate: '' };

const currentMonthRange = () => ({
  startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
  endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
});

const LogisticTrackingPage = () => {
  useBreadcrumb([
    { label: 'CRM', to: '/dashboard/crm' },
    { label: 'Logistic Tracking' }
  ]);

  const [, setSelected] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('tracking_status') || '';
  // The API limits a single-scope user anyway; a URL naming the other scope
  // would only show an empty list with no switch to undo it.
  const canSwitchScope = useShipScopes().length > 1;
  const shipScopeFromUrl = canSwitchScope ? (searchParams.get('ship_scope') || '') : '';

  // Opening the page cold shows the month CSD is working on; arriving from a
  // dashboard tile does not, because that tile counted over its own period and
  // a silent month filter here would show fewer rows than the number clicked.
  const [filters, setFilters] = useState(() => ({
    search: '',
    etdRange: statusFromUrl ? EMPTY_RANGE : currentMonthRange(),
    bookingRange: EMPTY_RANGE,
    actualRange: EMPTY_RANGE,
    incharge: [],
    status: statusFromUrl,
    shipScope: shipScopeFromUrl,
  }));

  const advancedParams = useRef({});
  const [showAdvanceFilter, setShowAdvanceFilter] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [pageReady, setPageReady] = useState(false);

  const { loading, allData, listError } = useSelector((state) => state.crm_logistic_tracking);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const activeFilterCount = allData?.filterTags?.length || 0;

  const buildParams = useCallback((current) => {
    const params = { ...advancedParams.current, page: 1, per_page: PERPAGE_DEFAULT_SM };

    if (current.search) {
      params.project_j_number_or_project_company_name_or_plan_names_cont = current.search;
    }
    // crm_shipment_plans has no `csd_committed` column — the real one is `etd`,
    // synced FROM deal_proj_shipments.csd_committed.
    if (current.etdRange?.startDate && current.etdRange?.endDate) {
      params.shipment_etd_gteq = current.etdRange.startDate;
      params.shipment_etd_lteq = current.etdRange.endDate;
    }
    if (current.bookingRange?.startDate && current.bookingRange?.endDate) {
      params.release_booking_estimate_time_of_departure_gteq = current.bookingRange.startDate;
      params.release_booking_estimate_time_of_departure_lteq = current.bookingRange.endDate;
    }
    if (current.actualRange?.startDate && current.actualRange?.endDate) {
      params.actual_etd_gteq = current.actualRange.startDate;
      params.actual_etd_lteq = current.actualRange.endDate;
    }
    // The LOGISTIC owner on the row (crm_logistics.incharge), not the project's
    // sales in-charge — those are different people and different columns.
    if (current.incharge?.length) {
      // Crm::Logistic::Tracking upcases `incharge` on write, so a directory entry
      // stored in lower case would silently match nothing.
      params.incharge_in = current.incharge.map(option => String(option.value).toUpperCase());
    }
    if (current.status) {
      params.tracking_status = current.status;
    }
    // Local / Oversea. The API already limits a user to the scopes they may see.
    if (current.shipScope) {
      params.ship_scope_eq = current.shipScope;
    }
    return params;
  }, []);

  // One fetch per settled filter state. The search box is the only field a user
  // types into, so it is the only one that waits.
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 400);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const effectiveFilters = useMemo(
    () => ({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch]
  );

  const [advancedRevision, setAdvancedRevision] = useState(0);

  // Keyed on the built params, not on the filter object: typing in the search
  // box makes a new filters object on every keystroke while the debounced value
  // — and therefore the request — has not changed.
  // advancedRevision looks unused to the linter, but it is the only signal that
  // the advancedParams ref buildParams reads has just been replaced.
  const paramsKey = useMemo(
    () => JSON.stringify(buildParams(effectiveFilters)),
    [effectiveFilters, advancedRevision, buildParams] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const params = JSON.parse(paramsKey);
    dispatch(setNestedParams({ key: ENTRY_KEY, params }));
    dispatch(getLogisticTrackings(params));
  }, [paramsKey, dispatch]);

  // Keep the shareable URL in step with the status the grid is actually showing.
  useEffect(() => {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      if (filters.status) next.set('tracking_status', filters.status);
      else next.delete('tracking_status');
      if (filters.shipScope) next.set('ship_scope', filters.shipScope);
      else next.delete('ship_scope');
      return next;
    }, { replace: true });
  }, [filters.status, filters.shipScope, setSearchParams]);

  const handleFilterChange = useCallback(patch => setFilters(current => ({ ...current, ...patch })), []);

  const handleResetFilters = useCallback(() => {
    advancedParams.current = {};
    setFilters({
      search: '',
      etdRange: EMPTY_RANGE,
      bookingRange: EMPTY_RANGE,
      actualRange: EMPTY_RANGE,
      incharge: [],
      status: '',
      shipScope: '',
    });
    setAdvancedRevision(revision => revision + 1);
  }, []);

  const handleReload = useCallback(() => {
    dispatch(getLogisticTrackings(allData?.currentParams || {}));
  }, [dispatch, allData]);

  const handleDownload = useCallback(async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      await dispatch(exportData(allData?.currentParams || {}));
    } finally {
      setIsExporting(false);
    }
  }, [dispatch, allData, isExporting]);

  // There is no Tracking "update" permission code on the profile, so the button
  // follows the rows: the API sends allow_edit per row and enforces it again on
  // import, so this only decides whether the button is worth showing.
  const canImport = useMemo(
    () => (allData?.data || []).some(row => row.permissions?.allow_edit),
    [allData]
  );

  useEffect(() => { setPageReady(true); }, []);

  if (!pageReady) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50 tw-gap-4">
        <LoadingSpinner size="lg" />
        <p className="tw-text-sm tw-text-gray-500 tw-animate-pulse">Loading Logistic Tracking…</p>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* ── Header: title + Back / Reload / Export ── */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div
              className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0"
              style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }}
            />
            <div className="tw-flex tw-items-center tw-gap-2">
              <LuShip className="tw-text-sky-500" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Logistic Tracking</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Track and manage logistics shipments for the CSD department</p>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              onClick={() => navigate(-1)}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-solid tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer"
            >
              <FaArrowLeft size={12} /> Back
            </button>
            <button
              onClick={handleReload}
              disabled={loading}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-solid tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer disabled:tw-opacity-50"
            >
              <FaSyncAlt size={12} className={loading ? 'tw-animate-spin' : ''} /> Reload
            </button>
            <button
              onClick={handleDownload}
              disabled={isExporting}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-solid tw-border-green-300 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-text-sm tw-font-medium tw-cursor-pointer disabled:tw-opacity-50"
            >
              <FaFileExcel size={13} /> {isExporting ? 'Exporting…' : 'Export'}
            </button>
            {canImport && (
              <button
                onClick={() => setShowImport(true)}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-solid tw-border-primary-300 tw-bg-primary-50 tw-text-primary-700 hover:tw-bg-primary-100 tw-text-sm tw-font-medium tw-cursor-pointer"
              >
                <FaFileImport size={12} /> Import
              </button>
            )}
          </div>
        </div>
      </div>

      <LogisticTrackingFilterBar
        filters={filters}
        onChange={handleFilterChange}
        onReset={handleResetFilters}
        onOpenAdvanced={() => setShowAdvanceFilter(true)}
        advancedCount={activeFilterCount}
      />

      {/* Table card */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-solid tw-border-slate-200 tw-overflow-hidden tw-relative">
        {loading && (
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/70 tw-z-10">
            <LoadingSpinner size="md" />
          </div>
        )}
        {listError && !loading && (
          <div role="alert" className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-bg-red-50 tw-border-0 tw-border-b tw-border-solid tw-border-red-200 tw-text-sm tw-text-red-700">
            <LuAlertTriangle size={16} className="tw-shrink-0" aria-hidden="true" />
            <span className="tw-flex-1">
              Could not load the Logistic tracking list ({listError}). No rows are shown until it loads.
            </span>
            <button
              type="button"
              onClick={handleReload}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-sm tw-border tw-border-solid tw-border-red-300 tw-bg-white tw-text-red-700 hover:tw-bg-red-100 tw-text-xs tw-font-semibold tw-cursor-pointer"
            >
              <FaSyncAlt size={11} /> Try again
            </button>
          </div>
        )}
        <Suspense fallback={
          <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
            <LoadingSpinner size="md" />
          </div>
        }>
          <LazyLogisticTracking setSelected={setSelected} />
        </Suspense>
      </div>

      <ImportLogisticTrackingDrawer
        show={showImport}
        onHide={() => setShowImport(false)}
        templateParams={allData?.currentParams || {}}
        onImported={handleReload}
      />

      {/* Advanced filter drawer (unchanged) */}
      <LogisticTrackingFilterAdvanceModal
        showModal={showAdvanceFilter}
        setshowModal={setShowAdvanceFilter}
        data={allData}
        entryKey={ENTRY_KEY}
        onApply={(applied) => {
          advancedParams.current = applied;
          setAdvancedRevision(revision => revision + 1);
        }}
        onReset={() => {
          advancedParams.current = {};
          setAdvancedRevision(revision => revision + 1);
        }}
      />
    </div>
  );
};

export default LogisticTrackingPage;
